// R11-3 — THE DISPATCHER. WHICH READING TYPE REACHES WHICH VIEW.
//
// This is the prize of roadmap 7's report family and the reason the other ten shells came out first:
// EVERY generated surface on this site is delivered through this one function. A member's birth
// chart, her tarot pull, her Human Design bodygraph, her astrocartography map, her petition, her
// Cauldron working — each of them is a `type` string written by the server and a branch in here, and
// nothing else decides what she is shown.
//
// **AND UNTIL NOW NOTHING COULD CHECK THAT.** The dispatch table lived in a 15,000-line TSX file, so
// the only instrument that could reach it was a regex over its own source text — which can see that a
// branch is written and can never see that the branch is REACHED, or that a type the server writes has
// no branch at all. A missing branch does not throw and does not log: it falls through to the generic
// narrative shell, and the member gets a reading that renders without the thing that makes it that
// reading (no cards, no bodygraph, no parchment). Out here `scripts/r11-dispatch.mjs` CALLS this
// function once per type string TAKEN FROM THE SERVER and reads which component came back.
//
// THE FALLBACK IS DELIBERATE AND IT IS THE LAST LINE: an unknown type still renders a real reading
// rather than a blank panel, because a report saved by a version of the server that this bundle has
// never heard of is a thing that happens on every deploy, and a member reading her own shelf must
// never meet an empty box. Chart-SHAPED data (it carries `planets`) goes to the chart report even
// when its type string is unrecognised; everything else goes to the narrative shell.
//
// Moved VERBATIM, every branch and every comment. `ReportPrintPortal` stays in App.tsx: it calls
// createPortal, which the gate's stub runtime has no answer for.

import { NarrativeReportView, type NarrativeReportData } from "./NarrativeReportView";
import { ReportView, type ReportData } from "./ReportView";
import { HumanDesignView, HumanDesignCompatView, type HumanDesignReportData, type HumanDesignCompatData } from "./HumanDesignView";
import { AcgMapView, AcgLocationView, AcgRelocationView, type AcgMapData, type AcgLocationData, type AcgRelocationData } from "./AcgViews";
import { TarotReadingView, TarotSpreadView, type TarotReadingData, type TarotSpreadData } from "./TarotViews";
import { DeepReportView, type DeepReportData } from "./DeepReportView";
import { PetitionPaperView, type PetitionPaperData } from "./PetitionPaperView";
import { AstroReadingView, type AstroReadingData } from "./AstroReadingView";
// IT #198/#199 — the seal's save goes through the ONE transport, never a bare anchor. See below.
import { downloadFromUrl } from "../lib/downloads";

// Every shape a delivered reading can arrive in. App.tsx imports it back: forty-odd flows in there
// hold one of these in state.
export type AnyReport = AstroReadingData | ReportData | NarrativeReportData | TarotReadingData | HumanDesignReportData | HumanDesignCompatData | TarotSpreadData | PetitionPaperData;

// Picks the right renderer for a report by its type/shape.
export function ReportBody({ data }: { data: AnyReport }) {
  const type = (data as { type?: string }).type;

  // =============================================================================================
  // >>> **THE APPROVED ASTROLOGY LAYOUT, DISPATCHED ON SHAPE AND NOT ON A LIST OF TYPES.** <<<
  // (her amendment, 2026-09-06/07)
  //
  // Anything carrying `sections` in the standard's shape is drawn her way. **That is what makes a
  // future reading inherit the layout by existing** rather than by somebody remembering to add a
  // line here — the same reason `src/astroReadingStandard.js` rosters the EXCEPTIONS rather than
  // the members. A type list would have to be edited for every reading she ever adds, and the one
  // that gets missed is always the one written next.
  //
  // **IT IS FIRST, ABOVE EVERY TYPE BRANCH, AND THAT IS DELIBERATE.** A reading rebuilt to the
  // standard must not be caught by the older branch for its own type on the way past.
  //
  // **AND IT DOES NOT REPLACE THE OLD VIEWS.** 3,374 birth charts are already saved in the old
  // shape and those rows are members' own readings; they carry no `sections` and fall through to
  // exactly the renderer they have always had.
  // =============================================================================================
  const asAstro = data as AstroReadingData;
  if (Array.isArray(asAstro.sections) && asAstro.sections.some((s) => s && s.body && s.title)) {
    return <AstroReadingView data={asAstro} />;
  }

  if (type === "astrology-report") return <DeepReportView data={data as DeepReportData} />;
  if (type === "astrocartography-map") return <AcgMapView data={data as AcgMapData} />;
  if (type === "astrocartography-location") return <AcgLocationView data={data as AcgLocationData} />;
  if (type === "astrocartography-relocation") return <AcgRelocationView data={data as AcgRelocationData} />;
  if (type === "tarot") return <TarotReadingView data={data as TarotReadingData} />;
  if (type === "tarot-spread") return <TarotSpreadView data={data as TarotSpreadData} />;
  if (type === "human-design-compat") return <HumanDesignCompatView data={data as HumanDesignCompatData} />;
  if (type === "human-design") return <HumanDesignView data={data as HumanDesignReportData} />;
  if (type === "keys-working") {
    // MR-K / MRK-7: a grounded Key of Solomon working with its real, indexed pentacle seal shown alongside
    // the text — named in a caption and downloadable full-size (the same image the PDF carries).
    const seal = (data as { seal?: { name?: string; slug?: string; imageUrl?: string } }).seal;
    const url = (data as { sealImageUrl?: string }).sealImageUrl || seal?.imageUrl;
    return (
      <NarrativeReportView
        data={data as NarrativeReportData}
        extras={url ? (
          <section className="keys-seal">
            <img src={url} alt={seal?.name ? `The ${seal.name}` : "The pentacle seal"} className="keys-seal__img" />
            {seal?.name ? <p className="keys-seal__caption">{seal.name}</p> : null}
            {/* >>> **IT #198/#199 — A BUTTON, NEVER AN ANCHOR, AND SEALCARD ALREADY LEARNED THIS
                (R101/R88/#127).** <<<
                `ic318@aol.com`, twice in one hour, in her own words: *"Member is receiving seals as
                links instead of images. When she taps the link it opens an astrology app and she
                loses the seal."* And to Lala, live: *"It keeps taking me to an astrology app, and I
                lose all my seals that you sent me what is going on this is crazy."*
                **A BARE `<a download>` ON A PHONE IS A HANDOFF TO THE OPERATING SYSTEM.** iOS and
                Android largely ignore the `download` attribute and give the file to whatever app
                claims the type -- for her, an astrology app -- and the seal is gone. `SealCard` was
                moved off an anchor for exactly this reason and this surface was left behind, which
                is R28/R29 in its most expensive shape: one house, two answers, and only one of them
                repaired.
                **THE IMAGE ABOVE IS UNTOUCHED AND IS THE REAL ANSWER TO HER TICKET** -- her fix in
                her own words is *"display as actual images ... so she can screenshot them"*, and it
                already renders inline. What is repaired is the trap sitting underneath it. */}
            <button
              type="button"
              className="keys-seal__download"
              onClick={() => { void downloadFromUrl(url); }}
            >
              Download the seal
            </button>
          </section>
        ) : undefined}
      />
    );
  }
  // PR1 — a palm reading: the narrative with the member's own palm photo shown beside it.
  if (type === "palm-reading") {
    const url = (data as { palmImageUrl?: string }).palmImageUrl;
    return (
      <NarrativeReportView
        data={data as NarrativeReportData}
        extras={url ? (<section className="palm-shot"><img src={url} alt="Your palm" className="palm-shot__img" /></section>) : undefined}
      />
    );
  }
  // MR-P3: a petition renders as an actual petition PAPER (parchment panel), not a report/letter.
  if (type === "petition") return <PetitionPaperView data={data as PetitionPaperData} />;
  // MR-O2: a Spell Your Oils working. The conjure modes carry the real pentacle seal full-size (MRK-7).
  if (type === "oils-working") {
    const seal = (data as { seal?: { name?: string; slug?: string; imageUrl?: string } }).seal;
    const url = (data as { sealImageUrl?: string }).sealImageUrl || seal?.imageUrl;
    return (
      <NarrativeReportView
        data={data as NarrativeReportData}
        extras={url ? (
          <section className="keys-seal">
            <img src={url} alt={seal?.name ? `The ${seal.name}` : "The pentacle seal"} className="keys-seal__img" />
            {seal?.name ? <p className="keys-seal__caption">{seal.name}</p> : null}
            {/* IT #198/#199: a bare `<a download>` on a phone is a handoff to the OS, which gives the
                file to whatever app claims the type -- an astrology app, for her -- and the seal is
                gone. The ONE transport instead (R28/R29; `SealCard` learned this at #127). */}
            <button type="button" className="keys-seal__download" onClick={() => { void downloadFromUrl(url); }}>
              Download the seal
            </button>
          </section>
        ) : undefined}
      />
    );
  }
  // MR-M: a grounded Books of Moses working (text-only; no Moses seal images in the DB yet).
  if (type === "moses-working") return <NarrativeReportView data={data as NarrativeReportData} />;
  // MR-H: grounded herbs & tools guidance.
  if (type === "herb-working") return <NarrativeReportView data={data as NarrativeReportData} />;
  if (type === "note") return <NarrativeReportView data={data as NarrativeReportData} />;
  // L11 — **THE READING THAT BROUGHT HER HERE, AND ITS BRANCH SHIPS IN THE SAME PUSH AS THE ROW.**
  // FUNNEL-QC-3's ruling: a reading reaches a shelf through `saveReport`, which files it under a
  // TYPE, and this dispatcher renders a type it has a branch for — so delivering one without a
  // branch puts a row on a brand-new member's shelf that opens to NOTHING, on the first screen she
  // ever sees. It is a narrative reading like every other cast one, so it renders through the shell
  // a dozen rooms already share rather than gaining a view of its own.
  if (type === "funnel-reading") return <NarrativeReportView data={data as NarrativeReportData} />;
  // MR-C: the compiled cauldron working, with its seal(s) shown alongside.
  // N1-1 (owner ruling): the Cauldron delivers the FINAL COMBINED SPELL and nothing else — so it is
  // headed "Your Spell", not "Your Reading". The commentary sections are already gone server-side.
  if (type === "cauldron-working") {
    const url = (data as { sealImageUrl?: string }).sealImageUrl;
    return (
      <NarrativeReportView data={data as NarrativeReportData} bodyHeading="Your Spell"
        extras={url ? (<section className="keys-seal"><img src={url} alt="The seal" className="keys-seal__img" /></section>) : undefined} />
    );
  }
  const isChart = type === "birth-chart" || Array.isArray((data as ReportData).planets);
  return isChart ? <ReportView data={data as ReportData} /> : <NarrativeReportView data={data as NarrativeReportData} />;
}
